import React from "react";
import { Check, ArrowRight } from "lucide-react";
import { hero, images, company } from "../../mock";
import { Button } from "../ui/button";
import BookingForm from "../BookingForm";
import TickerBar from "../TickerBar";

export default function Hero() {
  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

  return (
    <section className="relative pt-20">
      <TickerBar />
      <div className="relative overflow-hidden">
        {/* soft decorative accents */}
        <div className="pointer-events-none absolute -top-24 -right-24 h-80 w-80 rounded-full bg-gold/10 blur-3xl" />
        <div className="pointer-events-none absolute bottom-0 -left-24 h-72 w-72 rounded-full bg-gold/10 blur-3xl" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-14 items-start">
            {/* Left */}
            <div>
              <span className="inline-flex items-center gap-2 rounded-full border border-gold/40 bg-gold-soft px-4 py-1.5 text-xs font-semibold uppercase tracking-widest text-gold-dark">
                <span className="h-1.5 w-1.5 rounded-full bg-gold" /> {hero.badge}
              </span>

              <h1 className="mt-5 font-display font-700 text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-ink">
                {hero.titleParts.map((p, i) =>
                  p.gold ? (
                    <span key={i} className="gold-text">{p.text}</span>
                  ) : (
                    <span key={i}>{p.text}</span>
                  )
                )}
              </h1>

              <p className="mt-5 text-lg text-muted-foreground max-w-xl leading-relaxed">
                {hero.subtitle}
              </p>

              <div className="mt-7 flex flex-wrap gap-3">
                <Button
                  onClick={() => scrollTo("book-form")}
                  className="h-12 px-7 rounded-full bg-gold hover:bg-gold-dark text-ink font-semibold shadow-[0_8px_22px_rgba(201,162,74,0.4)]"
                >
                  Book a Free Site Visit
                </Button>
                <Button
                  onClick={() => scrollTo("how-it-works")}
                  variant="outline"
                  className="h-12 px-7 rounded-full border-ink text-ink hover:bg-ink hover:text-white font-semibold group"
                >
                  How It Works <ArrowRight size={17} className="ml-1.5 group-hover:translate-x-1 transition-transform" />
                </Button>
              </div>

              <ul className="mt-7 grid grid-cols-2 gap-3 max-w-md">
                {hero.bullets.map((b) => (
                  <li key={b} className="flex items-center gap-2 text-sm font-medium text-ink">
                    <span className="grid place-items-center h-5 w-5 rounded-full bg-gold/15 text-gold-dark shrink-0">
                      <Check size={13} />
                    </span>
                    {b}
                  </li>
                ))}
              </ul>

              <div className="mt-9 relative rounded-2xl overflow-hidden shadow-2xl ring-1 ring-gold/25 bg-[#1a1508]">
                <img src={images.heroMachine} alt="Elite Vending black and gold vending machine" className="w-full h-[520px] object-cover object-center scale-105" />
                <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-ink/85 to-transparent p-5">
                  <p className="text-white text-sm font-medium">
                    Modern, professional machines with Tap &amp; Go — stocked, serviced and monitored by us.
                  </p>
                </div>
              </div>
            </div>

            {/* Right — form */}
            <div id="book-form" className="lg:sticky lg:top-28">
              <div className="rounded-2xl border border-gold/25 bg-white shadow-[0_20px_60px_rgba(0,0,0,0.10)] overflow-hidden">
                <div className="bg-ink px-6 py-5">
                  <p className="text-gold text-xs font-semibold uppercase tracking-widest">Book a Free Site Visit</p>
                  <h2 className="mt-1 font-display text-2xl text-white">Zero-cost install for qualifying sites</h2>
                  <p className="mt-1 text-sm text-white/60">
                    Fill this out and we'll confirm eligibility and reach out within one business day.
                  </p>
                </div>
                <div className="p-6">
                  <BookingForm />
                </div>
              </div>
              <a href={company.phoneHref} className="mt-4 block text-center text-sm text-muted-foreground">
                Prefer to talk? <span className="font-semibold text-ink hover:text-gold">Call {company.phone}</span>
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
